import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, InputLabel, MenuItem, Select, Stack, TextField, Typography,
} from '@mui/material';
import { LookupsApi } from './api';
import type { LookupInlineEditorHandle } from './LookupInlineEditor';
import type { CalcType, Calculation, LookupSummary } from './types';
import {
  CALC_LABELS,
  ConditionalForm, ExpressionForm, LookupForm, SqlForm,
  buildConditional, buildLookup, parseConditional, parseLookup,
} from './CalcForms';

interface Props {
  open: boolean;
  /** the calc being edited, or null when adding a new one. */
  calc: Calculation | null;
  /** caption shown in the dialog title prefix. */
  title?: string;
  availableColumns: string[];
  /** Returns `{ok:true}` to close the dialog, or `{ok:false,error:string}`
   *  to keep it open and display the error inline. */
  onSave: (next: Calculation) => Promise<{ ok: boolean; error?: string }>;
  onCancel: () => void;
}

const EMPTY: Calculation = { col_name: '', calc_type: 'E', calc_expression: '' };

export default function CalculationEditDialog({
  open, calc, title, availableColumns, onSave, onCancel,
}: Props) {
  const [draft, setDraft] = useState<Calculation>(calc || EMPTY);
  const [lookups, setLookups] = useState<LookupSummary[]>([]);
  const [savingErr, setSavingErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const seededForRef = useRef<Calculation | null | undefined>(undefined);
  const lookupEditorRef = useRef<LookupInlineEditorHandle | null>(null);

  // Reset draft synchronously when the dialog opens or the underlying calc
  // changes identity. Doing this during render (guarded by the ref) means
  // the user never sees the previous form for even one frame — useEffect
  // would only reset *after* the stale render commits.
  if (open && seededForRef.current !== calc) {
    seededForRef.current = calc;
    setDraft(calc ? { ...calc } : { ...EMPTY });
  }
  if (!open && seededForRef.current !== undefined) {
    seededForRef.current = undefined;
  }

  useEffect(() => {
    if (!open) return;
    LookupsApi.list().then(setLookups).catch(() => setLookups([]));
  }, [open]);

  const cond = useMemo(() => parseConditional(draft.calc_expression), [draft.calc_expression]);
  const lk = useMemo(() => parseLookup(draft.calc_expression), [draft.calc_expression]);

  const setType = (t: CalcType) => setDraft({ ...draft, calc_type: t, calc_expression: '' });

  const canSave = draft.col_name.trim().length > 0;

  return (
    <Dialog open={open} onClose={onCancel} fullWidth maxWidth="md">
      <DialogTitle sx={{ pb: 1 }}>
        {title || (calc ? `Edit calculation` : 'Add calculation')}
      </DialogTitle>
      <DialogContent dividers>
        <Stack direction="row" spacing={1.5} mb={2}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Type</InputLabel>
            <Select
              label="Type" value={draft.calc_type}
              onChange={(e) => setType(e.target.value as CalcType)}
            >
              {(Object.keys(CALC_LABELS) as CalcType[]).map((t) => (
                <MenuItem key={t} value={t}>{CALC_LABELS[t]}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="New column name"
            placeholder="e.g. commission_amount"
            size="small"
            value={draft.col_name}
            onChange={(e) => setDraft({ ...draft, col_name: e.target.value })}
            sx={{ flexGrow: 1 }}
          />
          <TextField
            label="Column heading"
            placeholder="Shown in the grid (optional)"
            size="small"
            value={draft.header ?? ''}
            onChange={(e) => setDraft({ ...draft, header: e.target.value })}
            sx={{ flexGrow: 1 }}
            helperText="Defaults to the column name if left blank"
          />
        </Stack>

        {draft.calc_type === 'E' && (
          <ExpressionForm
            value={draft.calc_expression}
            onChange={(expr) => setDraft({ ...draft, calc_expression: expr })}
            colOptions={availableColumns}
          />
        )}
        {draft.calc_type === 'C' && (
          <ConditionalForm
            condition={cond.condition} trueExpr={cond.trueExpr} falseExpr={cond.falseExpr}
            colOptions={availableColumns}
            onChange={(c, t, f) => setDraft({ ...draft, calc_expression: buildConditional(c, t, f) })}
          />
        )}
        {draft.calc_type === 'L' && (
          <LookupForm
            value={lk}
            colOptions={availableColumns}
            lookups={lookups}
            inlineEditorRef={lookupEditorRef}
            onChange={(next) => setDraft({
              ...draft,
              calc_expression: buildLookup(next.lookupTable, next.isDynamic, next.keyColumn, next.dateColumn),
            })}
            onLookupSaved={() => {
              // After a save, refresh the autocomplete list of names.
              LookupsApi.list().then(setLookups).catch(() => undefined);
            }}
          />
        )}

        {savingErr && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setSavingErr(null)}>
            {savingErr}
          </Alert>
        )}
        {draft.calc_type === 'S' && (
          <SqlForm
            value={draft.calc_expression}
            onChange={(expr) => setDraft({ ...draft, calc_expression: expr })}
            colOptions={availableColumns}
          />
        )}

        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 2 }}>
          Saving will re-run the preview so you can see the new result immediately.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!canSave || busy}
          onClick={async () => {
            setSavingErr(null);
            setBusy(true);
            try {
              // Persist any unsaved lookup-data edits first so the rerun
              // actually sees them. Throws on backend error -- in that
              // case we bail and surface the message instead of silently
              // re-running with stale lookup data.
              await lookupEditorRef.current?.flush();
            } catch (e) {
              setSavingErr(`Could not save lookup data: ${(e as Error).message}`);
              setBusy(false);
              return;
            }
            try {
              // The parent runs a preview against the tentative process.
              // If the formula has a parse/runtime error we keep the
              // dialog open and show the message right here instead of
              // silently closing and stashing the error elsewhere.
              const r = await onSave(draft);
              if (!r.ok) {
                setSavingErr(r.error || 'Validation failed.');
              }
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? 'Validating…' : 'Save & re-run'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
